import { Component, HostListener, OnInit } from '@angular/core';
import { SharedModule } from '../../shared/shared.module';
import { CommonModule } from '@angular/common';
import {
  NavigationItem,
  navigationItemsWithPatient,
} from '../../models/navigation-item-model';
import { pages } from '../../shared/labels';
import { PatientService } from '../../services/patient.service';
import {
  PatientAnamnesis,
  PatientDiagnoses,
  PatientEssentialInfo,
  PatientFamilyMember,
  PatientPharmacotherapy,
  anamnesisClinicHistoryFields,
  anamnesisPersonalFields,
} from '../../models/patient-model';
import { FormsModule } from '@angular/forms';
import { LabelService } from '../../services/label.service';
import { AnamnesisComponent } from '../../sections/anamnesis/anamnesis.component';
import { DiagnosisComponent } from '../../sections/diagnosis/diagnosis.component';
import { PharmacotherapyComponent } from '../../sections/pharmacotherapy/pharmacotherapy.component';
import { ErrorPanelService } from '../../services/error-panel.service';
import { ActivatedRoute } from '@angular/router';
import { SidebarService } from '../../services/sidebar.service';
import { PatientPharmacoterapiesService } from '../../services/patient-pharmacotherapies.service';

type Section = 'medicalHistory' | 'diagnosis' | 'pharmacotherapy';

@Component({
  selector: 'app-medical-history-page',
  standalone: true,
  imports: [
    SharedModule,
    CommonModule,
    FormsModule,
    AnamnesisComponent,
    DiagnosisComponent,
    PharmacotherapyComponent,
  ],
  templateUrl: './medical-history-page.component.html',
  styleUrl: './medical-history-page.component.scss',
})
export class MedicalHistoryPageComponent implements OnInit {
  // Navigation
  navigationItems: NavigationItem[] = [];

  // Patient info
  patientId: string = '';
  patientName: string = '';
  patientSurname: string = '';
  patientNameAndSurname: string = '';
  isPatientActive: boolean = false;

  // Section management
  activeSection: Section = 'medicalHistory';

  // Data
  diagnoses: PatientDiagnoses[] = [];
  familyMembers: PatientFamilyMember[] = [];
  pharmacotherapies: PatientPharmacotherapy[] = [];

  // Anamnesis
  anamnesisPersonalFields = anamnesisPersonalFields;
  anamnesisClinicHistoryFields = anamnesisClinicHistoryFields;
  anamnesis: PatientAnamnesis = {
    id: '',
    patient_id: '',
    occupation: '',
    hospitalizations_and_surgical_operations: '',
    marital_status: '',
    pharmacological_anamnesis: '',
    number_of_children: '',
    pathologies: '',
    sexual_orientation: '',
    substances: '',
    hobbies: '',
    psychomotor_development: '',
    age: '',
    sports: '',
    allergies: '',
    years_of_education: '',
    supports: '',
    previous_clinical_paths: '',
  };

  // UI state
  showActivePrinciplesPanel: boolean = false;
  showNewDiagnosisPanel: boolean = false;
  isLoading: boolean = false;
  modifyButtonText: string = 'edit';
  hasModifyButtonBeenClicked: boolean = false;
  fieldIsReadOnly: boolean = true;
  isMobile = window.innerWidth < 930;

  constructor(
    private route: ActivatedRoute,
    private patientService: PatientService,
    public labelService: LabelService,
    public errorPanelService: ErrorPanelService,
    private sidebarService: SidebarService,
    private patientPharmacoterapiesService: PatientPharmacoterapiesService
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      this.loadPatientData(params['patientId']);
    });
  }

  private loadPatientData(patientId: string): void {
    this.patientService.getPatientEssentialInfo(patientId).subscribe({
      next: (data: PatientEssentialInfo) => {
        this.patientId = data.id;
        this.patientName = data.first_name;
        this.patientSurname = data.last_name;
        this.isPatientActive = data.is_active;
        this.patientNameAndSurname = `${this.patientName} ${this.patientSurname}`;

        this.setupNavigation();
        this.loadInitialData();
      },
      error: (err) => {
        console.error('Error getting patient essential info:', err);
        this.errorPanelService.showErrorSnackBar(
          'Errore nel recuperare i dati del paziente'
        );
      },
    });
  }

  private setupNavigation(): void {
    this.navigationItems = this.sidebarService.updateNavigationItems(
      navigationItemsWithPatient,
      this.patientName,
      this.patientSurname,
      this.patientId,
      pages.medicalHistory
    );
  }

  private loadInitialData(): void {
    this.getMedicalHistory();
    this.getFamilyMembers();
  }

  // Section navigation
  displayMedicalHistorySection(): void {
    this.activeSection = 'medicalHistory';
  }

  displayDiagnosisSection(): void {
    this.activeSection = 'diagnosis';
    this.loadDiagnoses();
  }

  displayPharmaSection(): void {
    this.activeSection = 'pharmacotherapy';
    this.loadPharmacotherapies();
  }

  private loadDiagnoses(): void {
    this.isLoading = true;
    this.diagnoses = [];

    this.patientService.getDiagnoses(this.patientId).subscribe({
      next: (response) => {
        this.diagnoses = response;
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Qualcosa è andato storto', error);
        this.errorPanelService.showErrorSnackBar(
          'Errore nel recuperare le diagnosi'
        );
        this.isLoading = false;
      },
    });
  }

  private loadPharmacotherapies(): void {
    this.isLoading = true;
    this.pharmacotherapies = [];

    this.patientPharmacoterapiesService
      .getPatientPharmacoterapies(this.patientId)
      .subscribe({
        next: (response: PatientPharmacotherapy[]) => {
          this.pharmacotherapies = response;
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Errore nel recuperare le farmacoterapie', error);
          this.errorPanelService.showErrorSnackBar(
            'Errore nel recuperare le farmacoterapie'
          );
        },
      });
  }

  // Data loading
  getMedicalHistory(): void {
    this.patientService.getAnamnesis(this.patientId).subscribe({
      next: (response) => {
        this.anamnesis = response;
      },
      error: (error) => {
        console.error('Qualcosa è andato storto', error);
        this.errorPanelService.showErrorSnackBar(
          "Errore nel recuperare l'anamnesi"
        );
      },
    });
  }

  getFamilyMembers(): void {
    this.patientService.getFamilyMembers(this.patientId).subscribe({
      next: (response) => {
        this.familyMembers = response;
      },
      error: (error) => {
        console.error('Errore nel recuperare i membri della famiglia', error);
        this.errorPanelService.showErrorSnackBar(
          'Errore nel recuperare i membri della famiglia'
        );
      },
    });
  }

  // UI actions
  toggleActivePrinciplesPanel(): void {
    this.showActivePrinciplesPanel = !this.showActivePrinciplesPanel;
  }

  toggleNewDiagnosisPanel(): void {
    this.showNewDiagnosisPanel = !this.showNewDiagnosisPanel;
  }

  onModifyButtonClick(): void {
    this.hasModifyButtonBeenClicked = !this.hasModifyButtonBeenClicked;
    this.fieldIsReadOnly = !this.hasModifyButtonBeenClicked;
    this.modifyButtonText = this.hasModifyButtonBeenClicked ? 'save' : 'edit';
  }

  // Getters for template
  get isMedicalHistorySelected(): string {
    return this.activeSection === 'medicalHistory'
      ? 'selected'
      : 'not-selected';
  }

  get isDiagnosisSelected(): string {
    return this.activeSection === 'diagnosis' ? 'selected' : 'not-selected';
  }

  get isPharmacotherapySelected(): string {
    return this.activeSection === 'pharmacotherapy'
      ? 'selected'
      : 'not-selected';
  }

  @HostListener('window:resize')
  onResize(): void {
    this.isMobile = window.innerWidth < 930;
  }
}
